import { ref } from 'vue';
import axios from 'axios';
import { useQuasar } from 'quasar';

export function useRequestWrapper<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    statusMap?: Record<number, string>,
    onUserError?: (message: string | null, error: any) => void,
    minLoadingTimeout?: number
) {
    const quasar = useQuasar();
    const loading = ref(false);
    const result = ref<Awaited<ReturnType<T>> | null>();

    const handler = async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>> | undefined> => {
        try {
            loading.value = true;
            if (!minLoadingTimeout) {
                const r = await fn(...args);
                result.value = r;
                return r;
            } else
                return await Promise.allSettled([
                    fn(...args),
                    new Promise((r) => setTimeout(r, minLoadingTimeout)),
                ]).then(([fnResult]) => {
                    if (fnResult.status === 'fulfilled') {
                        result.value = fnResult.value;
                        return fnResult.value;
                    }
                    return Promise.reject(fnResult.reason);
                });
        } catch (e: Error | AxiosError | any) {
            let errorMessage = null;
            let subject = null;
            if (axios.isAxiosError(e)) {
                if (statusMap && e.response?.status && e.response.status in statusMap) {
                    if (onUserError) {
                        onUserError(statusMap[e.response.status], e);
                    } else {
                        errorMessage = statusMap[e.response.status];
                    }
                } else if (e.response?.status && e.response.status >= 500) {
                    console.error(e, e.response?.data);
                    onUserError?.(null, e);
                    errorMessage = 'Ошибка сервера. Пожалуйста, повторите попытку позже';
                    subject = e.response?.data?.message || undefined;
                } else if (e.response?.status && e.response.status >= 400 && e.response?.data?.message) {
                    if (onUserError && e.response.data.message) {
                        onUserError(e.response.data.message, e);
                    } else {
                        errorMessage = e.response.data.message;
                    }
                }
            } else if (e?.name === 'NetworkError') {
                onUserError?.(null, e);
                errorMessage = 'Проверьте подключение к интернету';
            } else {
                errorMessage = e?.message || 'Неизвестная ошибка';
            }
            if (errorMessage)
                quasar.notify({
                    position: 'top-right',
                    type: 'error',
                    color: 'red',
                    textColor: 'white',
                    message: errorMessage,
                    caption: subject,
                    icon: 'warning',
                });
        } finally {
            loading.value = false;
        }
    };

    return { handler, loading, result };
}
